/**
 * AI 助手相关类型定义
 * 用于避免循环依赖
 */

/**
 * 门店组件类型枚举
 * 所有可点击标题的组件都在此登记，便于在钩子里按 title 做 case、统一处理
 */
export enum StoreComponentType {
  ROOM_STATUS = "room-status", // 实时房态
  ORDER_TRAFFIC = "order-traffic", // 订单流速
  ROOM_STATUS_RECENTLY = "room-status-recently", // 远期房态
  PRICE_ALERT = "price-alert", // 价格倒挂
  REVPAR = "revpar", // 可售房收入
  CHANNEL_INFO = "channel-info", // 渠道收入
  CHANNEL_PRICE = "channel-price", // 渠道房价
  ROOM_HEALTH = "room-health", // 房态健康
  CUSTOMER_SOURCE = "customer-source", // 客源结构
  DISCOUNT_ANALYSIS = "discount-analysis", // 折扣率
  CHANNEL_ANALYSIS = "channel-analysis", // 渠道订单
  OCCUPANCY_ANALYSIS = "occupancy-analysis", // 出租率
  OPERATION_EFFICIENCY = "operation-efficiency", // 运营效率
  STORE_OVERVIEW = "store-overview", // 门店信息概览
}

/**
 * 数据源类型（统计卡片 / 门店组件）
 * 定义在此文件，供 treeDataUtils 与 useAiAssistant 共用，避免 useAiAssistant ↔ treeDataUtils 循环依赖导致 WebKit 报「Cannot access uninitialized variable」。
 */
export enum DataSourceType {
  STAT_CARD = "stat-card",
  STORE_COMPONENT = "store-component",
}

/** 统计卡片子类型（与统计卡片点击一致） */
export enum StatCardType {
  PRICE_ADJUSTMENT_STORES = "price-adjustment-stores",
  PRICE_ANOMALY = "price-anomaly",
  FULLY_BOOKED = "fully-booked",
  CUSTOMER_SOURCE_ANOMALY = "customer-source-anomaly",
  SALES_STOPPED_ANOMALY = "sales-stopped-anomaly",
  REVIEW_ANOMALY = "review-anomaly",
  MAJOR_EVENT_AREAS = "major-event-areas",
  OCCUPANCY_RATE_ANOMALY = "occupancy-rate-anomaly",
}

/** AI 树 / 数据源节点描述 */
export interface DataSource {
  id: string;
  type: DataSourceType;
  storeId?: string;
  componentType?: StoreComponentType;
  statCardType?: StatCardType;
  data: any;
  metadata: {
    label: string;
    timestamp?: string;
    [key: string]: any;
  };
}

/**
 * 时间方向：7天/30天/12月 是相对「当前」的过去还是未来
 */
export type TimeDirection = "past" | "future";

/**
 * 发给 AI 组件的统一数据包：当前 + 7天/30天/12月（按需）
 * 每个组件点击时都先通过接口或 buildComponentDataPackage 组织成此结构，再传给 AI
 */
export interface ComponentDataPackage {
  /** 时间方向：past=过去（近7天/近30天/近12月），future=未来（未来7天/30天/12月）；默认 past */
  timeDirection?: TimeDirection;
  /** 当前数据（必填） */
  current: any;
  /** 7 天数据：timeDirection=past 为近 7 天，future 为未来 7 天 */
  last7Days?: any;
  /** 30 天数据：past=近30天，future=未来30天 */
  last30Days?: any;
  /** 12 个月数据：past=近12月，future=未来12月 */
  last12Months?: any;
  /**
   * 价格建议专用上下文（仅房态组件且用于动态定价提示词时填充）
   * 对应提示词所需：外部因素、订单流速、追随机制、房型策略、整体出租率、剩余房数、当前时间等
   */
  priceSuggestionContext?: PriceSuggestionContext;
}

/**
 * 房型策略：抛售型（快速去化）/ 惜售型（控量提价）
 */
export type RoomStrategyType = "抛售型" | "惜售型";

/**
 * 订单流速：用于判断「订单流速变快则价格调整幅度提高」
 */
export type OrderFlowSpeed = "fast" | "normal" | "slow";

/**
 * 价格建议（动态定价）专用上下文
 * 与「编辑思考方式」中核心问题（动态定价提示词）配套的输入信息
 */
export interface PriceSuggestionContext {
  /** 当前时间（ISO 或 HH:mm），用于判断早晨拐点、晚上 10 点前后 */
  currentTime: string;
  /** 整体出租率（0–100），来自全店汇总行 */
  overallOccupancy: number;
  /** 剩余总房数（全店可售数量） */
  remainingTotalRooms: number;
  /** 订单流速：fast=变快（提高调整幅度），normal/slow */
  orderFlowSpeed?: OrderFlowSpeed;
  /** 外部因素描述，如「明天暴风雪」；人进不来建议下调，人出不去建议上调 */
  externalFactors?: string;
  /** 各房型策略：房型 label -> 抛售型 | 惜售型 */
  roomStrategies?: Record<string, RoomStrategyType>;
  /** 是否有追随者（同房型或最高房型价格提高时需无理由跟着提高） */
  hasFollower?: boolean;
  /** 是否有被追随者（其价格降低时最低价房型需同幅度降低） */
  hasFollowed?: boolean;
}

/**
 * 价格建议（动态定价）传入数据结构示例（用于编辑思考方式「传入的数据结构」）
 * 与核心问题中的动态定价提示词一一对应
 */
export const PRICE_SUGGESTION_INPUT_STRUCTURE = {
  storeId: "门店ID",
  componentType: "room-status",
  data: {
    timeDirection: "past | future",
    current: [
      {
        id: "房型行ID",
        label: "房型名称",
        progress: "出租率 0-100",
        quantity: "总房量",
        quantityOpen: "可售数量（剩余房数）",
        priceData: "12 个时间点价格（当前价/走势）",
        normalizedReferenceLines: "[{ label: '年同比', value }, { label: '周环比', value }] 历史同期参考",
        isStoreTotal: "是否全店汇总行",
      },
    ],
    last7Days: "近 7 天房态（可选，用于历史同期出租率/房价）",
    last30Days: "近 30 天房态（可选）",
    last12Months: "近 12 月房态（可选）",
    priceSuggestionContext: {
      currentTime: "当前时间，用于早晨拐点/晚上10点前后判断",
      overallOccupancy: "整体出租率 0-100",
      remainingTotalRooms: "剩余总房数",
      orderFlowSpeed: "fast | normal | slow，订单流速变快则调整幅度提高",
      externalFactors: "外部因素描述，如「明天暴风雪」",
      roomStrategies: "各房型策略：{ 房型名: '抛售型'|'惜售型' }",
      hasFollower: "是否有追随者（需跟着同幅提高）",
      hasFollowed: "是否有被追随者（最低价房型跟着同幅降低）",
    },
  },
} as const;

/**
 * 门店组件点击参数
 */
export interface StoreComponentClickParams {
  storeId: string;
  componentType: StoreComponentType;
  data: any; // 组件对应的数据，约定为 ComponentDataPackage
  metadata?: {
    label?: string;
    elementType?: string; // 元素类型（如：房型、时间段、日期、渠道等）
    elementValue?: string | number; // 元素值（如：套房、00:00-02:00、2026-01-12等）
    componentId?: string; // 组件ID（从配置中获取）
    [key: string]: any;
  };
  clearPrevious?: boolean; // 默认为 true（单击）
}
